import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Info,
  Loader2,
  MessageSquarePlus,
  Mic,
  Paperclip,
  Play,
  Pause,
  Reply as ReplyIcon,
  Send,
  Star,
  Trash2,
  X,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { cn } from '@/lib/utils';
import { storageFileUrl } from '@/lib/storage-url';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@easyflow/ui-frontend/components/ui/sheet';

import MobileConversationList, {
  type MobileConversationItem,
} from '@/components/mobile/messenger/MobileConversationList';
import MobileThreadView from '@/components/mobile/messenger/MobileThreadView';

import { usePushNavigation } from '@/hooks/usePushNavigation';
import { messengerApi } from '@/pages/messenger/lib/messengerApi';
import { usePresence } from '@/pages/messenger/hooks/usePresence';
import {
  EMOJI_LIST,
  formatMessageTime,
  getDateSeparator,
  getDraft,
  saveDraft,
} from '@/pages/messenger/utils/format';
import type {
  ChatMessage,
  ConversationSummary,
  MessageAttachment,
  ParticipantBrief,
  PresenceStatus,
} from '@/pages/messenger/types';

// Task #1719 — pagina mobile dedicata al Messenger Chat. Riusa hook,
// service REST e tipi del desktop (`messengerApi`, `usePresence`), si appoggia
// alle primitive mobile di #1718 (lista, thread view, top bar) e implementa
// un renderer messaggi tap-and-hold con sheet azioni (reazioni, reply, pin,
// favorite, elimina). Il dispatcher in `pages/messenger/MessengerChat.tsx`
// sceglie questa pagina quando `useIsMobile()` ritorna true.

type View = 'list' | 'thread';
type FilterType = 'all' | 'unread' | 'direct' | 'group' | 'channel';

interface ActionSheetState {
  message: ChatMessage;
  isMine: boolean;
}

const LONG_PRESS_MS = 380;

function presenceToBadge(status: PresenceStatus | null | undefined): 'online' | 'away' | 'offline' {
  if (status === 'online') return 'online';
  if (status === 'away' || status === 'in-call' || status === 'dnd') return 'away';
  return 'offline';
}

function presenceLabel(status: PresenceStatus | null | undefined): string {
  switch (status) {
    case 'online': return 'Online';
    case 'away': return 'Assente';
    case 'in-call': return 'In chiamata';
    case 'dnd': return 'Non disturbare';
    default: return 'Offline';
  }
}

function parseAttachments(att: ChatMessage['attachments']): MessageAttachment[] {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  try {
    const parsed: unknown = JSON.parse(att);
    return Array.isArray(parsed) ? (parsed as MessageAttachment[]) : [];
  } catch {
    return [];
  }
}

function isImageAttachment(a: MessageAttachment): boolean {
  return !!a.mime_type && a.mime_type.startsWith('image/');
}

function isVoiceAttachment(a: MessageAttachment): boolean {
  return a.kind === 'voice' || (!!a.mime_type && a.mime_type.startsWith('audio/'));
}

function attachmentUrl(a: MessageAttachment): string | null {
  // Task #4040 — prima ritornava la chiave R2 nuda (URL relativo rotto);
  // ora passa dal serving autenticato /api/storage/<chiave>.
  return storageFileUrl(a.url || a.cloud_path) || null;
}

// ─────────────────────────────────────────────────────────────────────────
// Bubble message renderer (mobile-tuned, supporta long-press → action sheet)
// ─────────────────────────────────────────────────────────────────────────

interface BubbleProps {
  msg: ChatMessage;
  isMine: boolean;
  showHeader: boolean;
  showAvatar: boolean;
  currentUserId: string | null;
  onLongPress: (msg: ChatMessage, isMine: boolean) => void;
  onReplyPreviewTap?: () => void;
  index: number;
}

function MobileMessageBubble({
  msg, isMine, showHeader, showAvatar, currentUserId, onLongPress, index,
}: BubbleProps) {
  const timerRef = useRef<number | null>(null);
  const movedRef = useRef(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const clear = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    movedRef.current = false;
    startRef.current = { x: t.clientX, y: t.clientY };
    clear();
    timerRef.current = window.setTimeout(() => {
      if (!movedRef.current) {
        if (navigator.vibrate) {
          try { navigator.vibrate(10); } catch { /* noop */ }
        }
        onLongPress(msg, isMine);
      }
    }, LONG_PRESS_MS);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t || !startRef.current) return;
    const dx = Math.abs(t.clientX - startRef.current.x);
    const dy = Math.abs(t.clientY - startRef.current.y);
    if (dx > 8 || dy > 8) {
      movedRef.current = true;
      clear();
    }
  };

  const onTouchEnd = () => clear();
  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onLongPress(msg, isMine);
  };

  const attachments = parseAttachments(msg.attachments);
  const senderName =
    [msg.sender_first_name, msg.sender_last_name].filter(Boolean).join(' ') || 'Utente';
  const reactionsAgg = useMemo(() => {
    const map = new Map<string, { count: number; mine: boolean }>();
    for (const r of msg.reactions ?? []) {
      const cur = map.get(r.emoji) ?? { count: 0, mine: false };
      cur.count += 1;
      if (r.user_id === currentUserId) cur.mine = true;
      map.set(r.emoji, cur);
    }
    return [...map.entries()];
  }, [msg.reactions, currentUserId]);

  return (
    <motion.div
      initial={aurora.listItemIn.initial}
      animate={aurora.listItemIn.animate}
      transition={{ ...aurora.listItemIn.transition, delay: aurora.stagger(Math.min(index, 12)) }}
      className={cn('flex w-full px-3', isMine ? 'justify-end' : 'justify-start', showHeader ? 'mt-3' : 'mt-0.5')}
      data-testid={`mobile-message-${msg.id}`}
    >
      {!isMine && (
        <div className="w-8 mr-2 shrink-0">
          {showAvatar && (
            msg.sender_avatar_url ? (
              <img src={msg.sender_avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
            ) : (
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
                style={{ background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})` }}
                aria-hidden
              >
                {senderName.slice(0, 1).toUpperCase()}
              </div>
            )
          )}
        </div>
      )}
      <div className="max-w-[78%] min-w-0">
        {!isMine && showHeader && (
          <div className={cn(aurora.type.caption, 'text-muted-foreground mb-0.5 px-1')}>{senderName}</div>
        )}
        <div
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          onContextMenu={onContextMenu}
          className={cn(
            'relative px-3 py-2 rounded-2xl shadow-sm select-none touch-manipulation',
            isMine
              ? 'bg-indigo-600 text-white rounded-br-md'
              : 'bg-card border border-border text-foreground rounded-bl-md',
          )}
          style={isMine ? { background: auroraPalette.indigo } : undefined}
        >
          {msg.reply_content && (
            <div
              className={cn(
                'mb-1.5 px-2 py-1 rounded-lg border-l-2 text-xs',
                isMine ? 'bg-white/15 border-white/60' : 'bg-muted border-border',
              )}
            >
              <div className={cn('font-semibold truncate', isMine ? 'text-white/90' : 'text-foreground/80')}>
                {msg.reply_sender_name || 'Risposta'}
              </div>
              <div className={cn('truncate opacity-80', isMine ? 'text-white/80' : 'text-muted-foreground')}>
                {msg.reply_content}
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className="flex flex-col gap-1.5 mb-1">
              {attachments.map((a, i) => {
                const url = attachmentUrl(a);
                if (isVoiceAttachment(a) && url) {
                  return <VoiceMini key={i} src={url} durationMs={a.duration_ms ?? null} isMine={isMine} />;
                }
                if (isImageAttachment(a) && url) {
                  return (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden">
                      <img src={url} alt={a.filename} className="max-h-72 w-auto object-cover" loading="lazy" />
                    </a>
                  );
                }
                return (
                  <a
                    key={i}
                    href={url ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs',
                      isMine ? 'bg-white/15 text-white' : 'bg-muted text-foreground',
                    )}
                  >
                    <Paperclip className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{a.filename}</span>
                  </a>
                );
              })}
            </div>
          )}

          {msg.content && (
            <div className={cn('whitespace-pre-wrap break-words text-sm leading-relaxed', isMine ? 'text-white' : 'text-foreground')}>
              {msg.content}
            </div>
          )}

          <div className={cn('mt-1 text-[10px] flex items-center gap-1 justify-end', isMine ? 'text-white/70' : 'text-muted-foreground')}>
            {msg.is_edited && <span className="italic">modificato</span>}
            <span>{formatMessageTime(msg.created_at)}</span>
          </div>
        </div>

        {reactionsAgg.length > 0 && (
          <div className={cn('flex flex-wrap gap-1 mt-1', isMine ? 'justify-end' : 'justify-start')}>
            {reactionsAgg.map(([emoji, info]) => (
              <span
                key={emoji}
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border',
                  info.mine ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-200'
                            : 'bg-muted border-border text-foreground',
                )}
              >
                <span>{emoji}</span>
                <span className="tabular-nums">{info.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function VoiceMini({ src, durationMs, isMine }: { src: string; durationMs: number | null; isMine: boolean }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState((durationMs || 0) / 1000);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => { if (Number.isFinite(a.duration)) setDuration(a.duration); };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { void a.play(); setPlaying(true); }
  };

  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  return (
    <div className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg', isMine ? 'bg-white/15' : 'bg-muted')}>
      <button
        type="button"
        onClick={toggle}
        className={cn('h-8 w-8 rounded-full flex items-center justify-center shrink-0',
          isMine ? 'bg-white/25 text-white' : 'bg-background text-foreground border border-border')}
        aria-label={playing ? 'Pausa' : 'Riproduci'}
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 min-w-[120px]">
        <div className={cn('h-1.5 rounded-full overflow-hidden', isMine ? 'bg-white/25' : 'bg-border')}>
          <div className={cn('h-full', isMine ? 'bg-white' : 'bg-indigo-500')} style={{ width: `${pct}%` }} />
        </div>
        <div className={cn('text-[10px] tabular-nums mt-0.5', isMine ? 'text-white/80' : 'text-muted-foreground')}>
          {fmt(playing ? current : duration)}
        </div>
      </div>
      <audio ref={ref} src={src} preload="metadata" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Composer mobile inline (textarea + allegati + voice + send)
// ─────────────────────────────────────────────────────────────────────────

interface MobileComposerProps {
  conversationId: string;
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
  onSendText: (content: string, replyToId?: string) => Promise<void>;
  onSendFiles: (files: File[]) => Promise<void>;
  onSendVoice: (blob: Blob, durationMs: number) => Promise<void>;
  onTyping: () => void;
}

function MobileComposer({
  conversationId, replyTo, onCancelReply, onSendText, onSendFiles, onSendVoice, onTyping,
}: MobileComposerProps) {
  const [text, setText] = useState<string>(() => getDraft(conversationId));
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recStartRef = useRef<number>(0);

  useEffect(() => { setText(getDraft(conversationId)); }, [conversationId]);

  const change = (v: string) => {
    setText(v);
    saveDraft(conversationId, v);
    onTyping();
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await onSendText(t, replyTo?.id);
      setText('');
      saveDraft(conversationId, '');
    } finally { setSending(false); }
  };

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (!files.length) return;
    setSending(true);
    try { await onSendFiles(files); } finally { setSending(false); }
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      recStartRef.current = Date.now();
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        const dur = Date.now() - recStartRef.current;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 0 && dur > 400) {
          setSending(true);
          try { await onSendVoice(blob, dur); } finally { setSending(false); }
        }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch {
      setRecording(false);
    }
  };

  const stopRecording = () => {
    const mr = recorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  return (
    <div className="px-2 py-2">
      {replyTo && (
        <div className="flex items-center gap-2 px-3 py-1.5 mb-2 rounded-xl bg-muted/70 border border-border">
          <ReplyIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-foreground/80 truncate">
              {replyTo.sender_first_name || 'Risposta'}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {replyTo.content || '…'}
            </div>
          </div>
          <button onClick={onCancelReply} aria-label="Annulla risposta" className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-background">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onPickFiles} />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileRef.current?.click()}
          disabled={sending || recording}
          className="h-11 w-11 rounded-full shrink-0"
          aria-label="Allega file"
          data-testid="mobile-chat-attach"
        >
          <Paperclip className="w-5 h-5" />
        </Button>
        <Textarea
          value={text}
          onChange={(e) => change(e.target.value)}
          placeholder={recording ? 'Registrazione vocale in corso…' : 'Scrivi un messaggio…'}
          disabled={recording}
          className="flex-1 min-h-[44px] max-h-[140px] resize-none rounded-2xl bg-muted/60 border-transparent focus-visible:border-border text-base leading-relaxed"
          rows={1}
          data-testid="mobile-chat-textarea"
        />
        {text.trim().length > 0 ? (
          <Button
            type="button"
            onClick={send}
            disabled={sending}
            className="h-11 w-11 rounded-full shrink-0 p-0"
            style={{ background: auroraPalette.indigo }}
            aria-label="Invia"
            data-testid="mobile-chat-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        ) : (
          <button
            type="button"
            onTouchStart={(e) => { e.preventDefault(); void startRecording(); }}
            onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
            onMouseDown={() => void startRecording()}
            onMouseUp={() => stopRecording()}
            onMouseLeave={() => recording && stopRecording()}
            className={cn(
              'h-11 w-11 rounded-full shrink-0 inline-flex items-center justify-center text-white transition-colors',
              recording ? 'bg-rose-500 animate-pulse' : '',
            )}
            style={!recording ? { background: auroraPalette.indigo } : undefined}
            aria-label={recording ? 'Rilascia per inviare' : 'Tieni premuto per registrare'}
            data-testid="mobile-chat-voice"
          >
            <Mic className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Pagina MobileChat
// ─────────────────────────────────────────────────────────────────────────

export default function MobileChat() {
  const { user } = useAuth();
  const { socket, joinRoom, leaveRoom, on, off, socketVersion } = useSocket();
  const tenantId = user?.tenant_id || null;
  const userId = user?.id || null;
  const { presenceMap } = usePresence(tenantId);

  const nav = usePushNavigation<View>({ initial: 'list' });

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [convLoading, setConvLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');

  const [selectedConv, setSelectedConv] = useState<ConversationSummary | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [actionSheet, setActionSheet] = useState<ActionSheetState | null>(null);

  // Nuova chat (sheet con elenco utenti)
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [tenantUsers, setTenantUsers] = useState<ParticipantBrief[]>([]);

  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Conversation loading ─────────────────────────────────────────────

  const loadConversations = useCallback(async () => {
    setConvLoading(true);
    try {
      const res = await messengerApi.listConversations({ type: filter });
      if (res.success && res.data) {
        let list = res.data;
        if (filter === 'unread') list = list.filter((c) => c.unread_count > 0);
        setConversations(list);
      }
    } finally {
      setConvLoading(false);
    }
  }, [filter]);

  useEffect(() => { void loadConversations(); }, [loadConversations]);

  // ─── Socket events ────────────────────────────────────────────────────

  useEffect(() => {
    const onNew = (msg: ChatMessage) => {
      if (selectedConv && msg.conversation_id === selectedConv.id) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        void messengerApi.markRead(selectedConv.id);
      }
      void loadConversations();
    };
    const onTypingEvt = (data: { conversationId: string; userId: string; userName: string; isTyping: boolean }) => {
      if (data.conversationId !== selectedConv?.id) return;
      setTypingUsers((prev) => {
        const n = { ...prev };
        if (data.isTyping) n[data.userId] = data.userName; else delete n[data.userId];
        return n;
      });
    };
    const onEdited = (msg: ChatMessage) => {
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    };
    const onDeleted = (data: { messageId: string }) => {
      setMessages((prev) => prev.filter((m) => m.id !== data.messageId));
    };
    const onReactionEvt = (data: { messageId: string; userId: string; emoji: string; action: 'add' | 'remove' }) => {
      setMessages((prev) => prev.map((m) => {
        if (m.id !== data.messageId) return m;
        const reactions = m.reactions ? [...m.reactions] : [];
        if (data.action === 'add') {
          reactions.push({ emoji: data.emoji, user_id: data.userId });
        } else {
          const idx = reactions.findIndex((r) => r.emoji === data.emoji && r.user_id === data.userId);
          if (idx >= 0) reactions.splice(idx, 1);
        }
        return { ...m, reactions };
      }));
    };

    on('chat:message', onNew);
    on('chat:typing', onTypingEvt);
    on('chat:message:edited', onEdited);
    on('chat:message:deleted', onDeleted);
    on('chat:reaction', onReactionEvt);
    return () => {
      off('chat:message', onNew);
      off('chat:typing', onTypingEvt);
      off('chat:message:edited', onEdited);
      off('chat:message:deleted', onDeleted);
      off('chat:reaction', onReactionEvt);
    };
  }, [selectedConv, on, off, loadConversations]);

  // ─── Self-healing: riconciliazione autoritativa al (ri)connect ─────────
  // Specchio del ramo desktop: dopo un buco del pub/sub Redis / reconnect del
  // socket, i listener ri-sottoscrivono solo eventi futuri. Senza refetch i
  // badge non letti e le spunte `read_by_count` restano stale. Refs per
  // scattare SOLO sul cambio di socketVersion, non a ogni cambio conversazione.
  const selectedConvRef = useRef<ConversationSummary | null>(null);
  useEffect(() => { selectedConvRef.current = selectedConv; }, [selectedConv]);
  const loadConversationsRef = useRef(loadConversations);
  useEffect(() => { loadConversationsRef.current = loadConversations; }, [loadConversations]);

  useEffect(() => {
    if (socketVersion === 0) return;
    void loadConversationsRef.current();
    const conv = selectedConvRef.current;
    if (!conv) return;
    void messengerApi.getMessages(conv.id, undefined, 50).then((res) => {
      if (res.success && res.data) {
        setMessages([...res.data].reverse());
      }
      void messengerApi.markRead(conv.id);
    });
  }, [socketVersion]);

  // ─── Open conversation ────────────────────────────────────────────────

  const openConversation = useCallback(async (conv: ConversationSummary) => {
    if (selectedConv?.id) leaveRoom(`chat:${selectedConv.id}`);
    setSelectedConv(conv);
    setMessages([]);
    setReplyTo(null);
    setHasMore(true);
    setLoadingMessages(true);
    nav.push('thread');
    joinRoom(`chat:${conv.id}`);
    try {
      const res = await messengerApi.getMessages(conv.id, undefined, 50);
      if (res.success && res.data) {
        const msgs = [...res.data].reverse();
        setMessages(msgs);
        setHasMore(msgs.length >= 50);
      }
    } finally {
      setLoadingMessages(false);
    }
    void messengerApi.markRead(conv.id).then(() => {
      setConversations((prev) => prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c)));
    });
  }, [selectedConv, joinRoom, leaveRoom, nav]);

  const backToList = useCallback(() => {
    if (selectedConv?.id) leaveRoom(`chat:${selectedConv.id}`);
    setSelectedConv(null);
    setMessages([]);
    setReplyTo(null);
    setTypingUsers({});
    nav.pop();
  }, [selectedConv, leaveRoom, nav]);

  // Quando lo stack pop arriva via gesture/back nativo, sincronizza lo stato.
  useEffect(() => {
    if (nav.view === 'list' && selectedConv) {
      if (selectedConv.id) leaveRoom(`chat:${selectedConv.id}`);
      setSelectedConv(null);
      setMessages([]);
      setReplyTo(null);
      setTypingUsers({});
    }
  }, [nav.view, selectedConv, leaveRoom]);

  const loadMore = useCallback(async () => {
    if (!selectedConv || loadingMore || !hasMore || messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const res = await messengerApi.getMessages(selectedConv.id, oldest.created_at, 50);
      if (res.success && res.data) {
        const older = [...res.data].reverse();
        if (older.length < 50) setHasMore(false);
        if (older.length > 0) setMessages((prev) => [...older, ...prev]);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [selectedConv, loadingMore, hasMore, messages]);

  // ─── Typing / send ─────────────────────────────────────────────────────

  const onTyping = useCallback(() => {
    if (!selectedConv || !socket) return;
    socket.emit('chat:typing', { conversationId: selectedConv.id, isTyping: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('chat:typing', { conversationId: selectedConv.id, isTyping: false });
    }, 2000);
  }, [selectedConv, socket]);

  const sendText = useCallback(async (content: string, replyToId?: string) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendMessage(selectedConv.id, { content, reply_to_id: replyToId });
    if (res.success && res.data) {
      const created = res.data;
      setMessages((prev) => (prev.some((m) => m.id === created.id) ? prev : [...prev, created]));
      setReplyTo(null);
    }
  }, [selectedConv]);

  const sendFiles = useCallback(async (files: File[]) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendFiles(selectedConv.id, files);
    if (res.success && res.data) {
      const created = res.data;
      setMessages((prev) => (prev.some((m) => m.id === created.id) ? prev : [...prev, created]));
    }
  }, [selectedConv]);

  const sendVoice = useCallback(async (blob: Blob, durationMs: number) => {
    if (!selectedConv) return;
    const res = await messengerApi.sendVoice(selectedConv.id, blob, durationMs);
    if (res.success && res.data) {
      const created = res.data;
      setMessages((prev) => (prev.some((m) => m.id === created.id) ? prev : [...prev, created]));
    }
  }, [selectedConv]);

  // ─── Message actions ───────────────────────────────────────────────────

  const toggleReaction = useCallback(async (msgId: string, emoji: string) => {
    const msg = messages.find((m) => m.id === msgId);
    const hasReacted = msg?.reactions?.some((r) => r.emoji === emoji && r.user_id === userId);
    if (hasReacted) await messengerApi.removeReaction(msgId, emoji);
    else await messengerApi.addReaction(msgId, emoji);
  }, [messages, userId]);

  const togglePin = useCallback(async (msgId: string, isPinned: boolean) => {
    await messengerApi.togglePin(msgId, isPinned);
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, is_pinned: !isPinned } : m)));
  }, []);

  const toggleFav = useCallback(async (msgId: string, isFav: boolean) => {
    await messengerApi.toggleFavorite(msgId, isFav);
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, is_favorited: !isFav } : m)));
  }, []);

  const deleteMessage = useCallback(async (msgId: string) => {
    await messengerApi.deleteMessage(msgId);
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
  }, []);

  // ─── Nuova chat ────────────────────────────────────────────────────────

  const searchUsers = useCallback(async (q: string) => {
    setUserSearch(q);
    const res = await messengerApi.searchUsers(q);
    if (res.success && res.data) setTenantUsers(res.data);
  }, []);

  const openNewChat = useCallback(() => {
    setNewChatOpen(true);
    void searchUsers('');
  }, [searchUsers]);

  const startDirectChat = useCallback(async (target: ParticipantBrief) => {
    const res = await messengerApi.createDirect(target.id);
    if (res.success && res.data) {
      setNewChatOpen(false);
      await loadConversations();
      void openConversation(res.data);
    }
  }, [loadConversations, openConversation]);

  // ─── Mapping verso primitive ───────────────────────────────────────────

  const conversationItems = useMemo<MobileConversationItem[]>(() => {
    return conversations.map((c) => {
      const other = c.other_participants?.[0];
      const presence = other ? presenceMap[other.id] : undefined;
      return {
        id: c.id,
        title: c.display_title || 'Conversazione',
        lastMessage: c.last_message_preview || c.last_message || null,
        lastMessageAt: c.last_message_at,
        unread: c.unread_count,
        avatarUrl: c.avatar_url || other?.avatar_url || null,
        presence: c.type === 'direct' ? presenceToBadge(presence) : undefined,
      };
    });
  }, [conversations, presenceMap]);

  const otherDirectStatus = useMemo<PresenceStatus | null>(() => {
    if (!selectedConv || selectedConv.type !== 'direct') return null;
    const other = selectedConv.other_participants?.[0];
    if (!other) return null;
    return presenceMap[other.id] || 'offline';
  }, [selectedConv, presenceMap]);

  const threadSubtitle = useMemo<string>(() => {
    const typingDisplay = Object.values(typingUsers).filter(Boolean);
    if (typingDisplay.length > 0) return `${typingDisplay.join(', ')} sta scrivendo…`;
    if (!selectedConv) return '';
    if (selectedConv.type === 'direct') return presenceLabel(otherDirectStatus);
    return `${selectedConv.participant_count || 0} membri`;
  }, [typingUsers, selectedConv, otherDirectStatus]);

  // ─── Render ────────────────────────────────────────────────────────────

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  let body: ReactNode;
  if (nav.view === 'list' || !selectedConv) {
    body = (
      <div className="flex flex-col h-full bg-background" data-testid="mobile-chat-root">
        <div
          className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center gap-2 px-3 py-2">
            <h1 className={cn(aurora.type.title, 'flex-1 text-foreground')}>Chat</h1>
            {totalUnread > 0 && (
              <span
                className="h-5 min-w-[20px] px-1.5 rounded-full text-white text-[10px] font-bold inline-flex items-center justify-center"
                style={{ background: auroraPalette.indigo }}
                aria-label={`${totalUnread} non letti`}
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
            <Button
              type="button"
              size="icon"
              onClick={openNewChat}
              className="h-10 w-10 rounded-full"
              style={{ background: auroraPalette.indigo }}
              aria-label="Nuova conversazione"
              data-testid="mobile-chat-new"
            >
              <MessageSquarePlus className="w-5 h-5 text-white" />
            </Button>
          </div>
          <div className="px-2 pb-2 flex gap-1 overflow-x-auto" data-testid="mobile-chat-filters">
            {(['all', 'unread', 'direct', 'group', 'channel'] as FilterType[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                  filter === f
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
              >
                {f === 'all' ? 'Tutte'
                  : f === 'unread' ? 'Non lette'
                    : f === 'direct' ? 'Dirette'
                      : f === 'group' ? 'Gruppi' : 'Canali'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <MobileConversationList
            items={conversationItems}
            loading={convLoading}
            onSelect={(item) => {
              const conv = conversations.find((c) => c.id === item.id);
              if (conv) void openConversation(conv);
            }}
            onRefresh={loadConversations}
          />
        </div>
      </div>
    );
  } else {
    body = (
      <MobileThreadView
        header={{
          title: selectedConv.display_title || 'Conversazione',
          subtitle: threadSubtitle,
          avatarUrl: selectedConv.avatar_url || selectedConv.other_participants?.[0]?.avatar_url || null,
          presence: selectedConv.type === 'direct' ? presenceToBadge(otherDirectStatus) : undefined,
        }}
        onBack={backToList}
        headerActions={
          <button
            type="button"
            className="h-11 w-11 inline-flex items-center justify-center rounded-full text-foreground/80 hover:bg-muted"
            aria-label="Info conversazione"
            data-testid="mobile-chat-info"
          >
            <Info className="w-5 h-5" />
          </button>
        }
        composer={
          <MobileComposer
            conversationId={selectedConv.id}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            onSendText={sendText}
            onSendFiles={sendFiles}
            onSendVoice={sendVoice}
            onTyping={onTyping}
          />
        }
      >
        <ThreadMessages
          messages={messages}
          loading={loadingMessages}
          loadingMore={loadingMore}
          hasMore={hasMore}
          currentUserId={userId}
          onLongPress={(m, isMine) => setActionSheet({ message: m, isMine })}
          onLoadMore={loadMore}
        />
      </MobileThreadView>
    );
  }

  return (
    <>
      {body}

      {/* Action sheet (long-press messaggio) */}
      <Sheet open={!!actionSheet} onOpenChange={(o) => !o && setActionSheet(null)}>
        <SheetContent side="bottom" className="p-0 gap-0 rounded-t-2xl" data-testid="mobile-chat-action-sheet">
          <SheetHeader className="px-4 pt-3 pb-2 border-b border-border">
            <SheetTitle className="text-sm text-muted-foreground font-normal">Azioni messaggio</SheetTitle>
          </SheetHeader>
          {actionSheet && (
            <div className="p-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}>
              <div className="grid grid-cols-8 gap-1 mb-3">
                {EMOJI_LIST.slice(0, 16).map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      void toggleReaction(actionSheet.message.id, emoji);
                      setActionSheet(null);
                    }}
                    className="h-10 w-10 rounded-full hover:bg-muted active:bg-muted/80 inline-flex items-center justify-center text-xl"
                    aria-label={`Reagisci ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div className="flex flex-col">
                <ActionRow
                  icon={<ReplyIcon className="w-4 h-4" />}
                  label="Rispondi"
                  onClick={() => { setReplyTo(actionSheet.message); setActionSheet(null); }}
                />
                <ActionRow
                  icon={<Star className={cn('w-4 h-4', actionSheet.message.is_favorited && 'fill-current text-amber-500')} />}
                  label={actionSheet.message.is_favorited ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                  onClick={() => { void toggleFav(actionSheet.message.id, !!actionSheet.message.is_favorited); setActionSheet(null); }}
                />
                <ActionRow
                  icon={<MessageSquarePlus className="w-4 h-4" />}
                  label={actionSheet.message.is_pinned ? 'Rimuovi pin' : 'Aggiungi al pin'}
                  onClick={() => { void togglePin(actionSheet.message.id, !!actionSheet.message.is_pinned); setActionSheet(null); }}
                />
                {actionSheet.isMine && (
                  <ActionRow
                    icon={<Trash2 className="w-4 h-4 text-rose-500" />}
                    label="Elimina"
                    destructive
                    onClick={() => { void deleteMessage(actionSheet.message.id); setActionSheet(null); }}
                  />
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Nuova chat */}
      <Sheet open={newChatOpen} onOpenChange={setNewChatOpen}>
        <SheetContent side="bottom" className="h-[85dvh] max-h-[85dvh] flex flex-col p-0 gap-0" data-testid="mobile-chat-newchat-sheet">
          <SheetHeader className="px-4 py-3 border-b border-border">
            <SheetTitle className="text-base">Nuova conversazione</SheetTitle>
          </SheetHeader>
          <div className="px-3 py-2 border-b border-border">
            <Input
              value={userSearch}
              onChange={(e) => searchUsers(e.target.value)}
              placeholder="Cerca utente…"
              className="h-10 rounded-full bg-muted/60 border-transparent focus-visible:border-border"
              autoFocus
            />
          </div>
          <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <AnimatePresence>
              {tenantUsers.filter((u) => u.id !== userId).map((u, idx) => {
                const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Utente';
                return (
                  <motion.button
                    key={u.id}
                    initial={aurora.listItemIn.initial}
                    animate={aurora.listItemIn.animate}
                    transition={{ ...aurora.listItemIn.transition, delay: aurora.stagger(Math.min(idx, 12)) }}
                    type="button"
                    onClick={() => startDirectChat(u)}
                    className="w-full flex items-center gap-3 px-3 py-3 hover:bg-muted active:bg-muted/80 border-b border-border/60 text-left"
                  >
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <div
                        className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                        style={{ background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})` }}
                        aria-hidden
                      >
                        {fullName.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={cn(aurora.type.bodyStrong, 'truncate text-foreground')}>{fullName}</div>
                      {u.email && (
                        <div className={cn(aurora.type.caption, 'truncate text-muted-foreground')}>{u.email}</div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Thread message renderer
// ─────────────────────────────────────────────────────────────────────────

interface ThreadMessagesProps {
  messages: ChatMessage[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  currentUserId: string | null;
  onLongPress: (msg: ChatMessage, isMine: boolean) => void;
  onLoadMore: () => void;
}

function ThreadMessages({
  messages, loading, loadingMore, hasMore, currentUserId, onLongPress, onLoadMore,
}: ThreadMessagesProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || loadingMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadMore();
    }, { rootMargin: '120px 0px 0px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, loading, onLoadMore]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento messaggi…
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center px-8 text-center text-muted-foreground">
        <p className={aurora.type.body}>Nessun messaggio. Rompi il ghiaccio scrivendo qui sotto.</p>
      </div>
    );
  }

  return (
    <div className="pb-3 pt-2">
      {hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-2">
          {loadingMore && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
      )}
      {messages.map((msg, idx) => {
        const prev = idx > 0 ? messages[idx - 1] : null;
        const sameDay = prev ? getDateSeparator(prev.created_at) === getDateSeparator(msg.created_at) : false;
        const sameSender = prev?.sender_id === msg.sender_id;
        const closeInTime = prev
          ? (new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime()) < 120_000
          : false;
        const showHeader = !sameSender || !sameDay || !closeInTime;
        const isMine = msg.sender_id === currentUserId;
        const showDateSep = !sameDay || !prev;
        const nextMsg = idx < messages.length - 1 ? messages[idx + 1] : null;
        const nextSame = nextMsg ? nextMsg.sender_id === msg.sender_id : false;
        const showAvatar = !isMine && !nextSame;

        return (
          <div key={msg.id}>
            {showDateSep && (
              <div className="flex items-center justify-center py-3">
                <div className="px-3 py-1 rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                  {getDateSeparator(msg.created_at)}
                </div>
              </div>
            )}
            <MobileMessageBubble
              msg={msg}
              isMine={isMine}
              showHeader={showHeader}
              showAvatar={showAvatar}
              currentUserId={currentUserId}
              onLongPress={onLongPress}
              index={idx}
            />
          </div>
        );
      })}
    </div>
  );
}

function ActionRow({
  icon, label, onClick, destructive,
}: { icon: ReactNode; label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted active:bg-muted/80 text-left',
        destructive ? 'text-rose-600' : 'text-foreground',
      )}
    >
      <span className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-muted/60">{icon}</span>
      <span className={aurora.type.body}>{label}</span>
    </button>
  );
}
